/* global Excel */

import { INSPECTION_STATUS } from '../FinanceOfficeOperationRegistry';
import {
  assertCurrentDocumentIdentity,
  assertNotAborted,
  cellIsEmpty,
  containsLicensedDataEvidence,
  excelRangeAddress,
  scalarMatches,
  targetDrift,
  verificationFailed,
  writerError,
} from './common';

function materializedCells(request) {
  const width = request.payload.cells[0].length;
  const separator = Array(width).fill(null);
  const sources = request.payload.citationLines.map((line) => [
    line,
    ...Array(Math.max(0, width - 1)).fill(null),
  ]);
  return [...request.payload.cells, separator, ...sources];
}

function operationRange(request) {
  const cells = materializedCells(request);
  return {
    cells,
    address: excelRangeAddress(
      request.target.anchorCell,
      cells.length,
      cells[0].length
    ),
  };
}

function rangeState(range, expectedCells) {
  let anyPopulated = false;
  let allExpected = true;
  for (let row = 0; row < expectedCells.length; row += 1) {
    for (let column = 0; column < expectedCells[row].length; column += 1) {
      const value = range.values?.[row]?.[column];
      const formula = range.formulas?.[row]?.[column];
      if (!cellIsEmpty(value, formula)) anyPopulated = true;
      if (
        (typeof formula === 'string' && formula.startsWith('=')) ||
        !scalarMatches(expectedCells[row][column], value)
      ) {
        allExpected = false;
      }
    }
  }
  if (!anyPopulated) return INSPECTION_STATUS.NOT_COMMITTED;
  return allExpected
    ? INSPECTION_STATUS.COMMITTED
    : INSPECTION_STATUS.INDETERMINATE;
}

export class ExcelFinanceWriter {
  constructor({
    run = (callback) => {
      if (typeof Excel === 'undefined' || typeof Excel.run !== 'function') {
        throw writerError(
          'finance_office_unavailable',
          'Excel API is unavailable'
        );
      }
      return Excel.run(callback);
    },
  } = {}) {
    this.run = run;
    this.supportsReadOnlyLiveDataTarget = true;
  }

  async detectLicensedDataEvidence() {
    return this.run(async (context) => {
      const worksheets = context.workbook.worksheets;
      worksheets.load('items/id');
      await context.sync();
      const ranges = worksheets.items.map((worksheet) => {
        const range = worksheet.getUsedRangeOrNullObject(true);
        range.load('isNullObject,values,text');
        return range;
      });
      await context.sync();
      return ranges.some((range) =>
        !range.isNullObject &&
        (
          containsLicensedDataEvidence(range.text) ||
          containsLicensedDataEvidence(range.values)
        )
      );
    });
  }

  async prepareLiveDataTarget({
    signal,
    assertCurrentIdentity,
  } = {}) {
    assertNotAborted(signal);
    return this.run(async (context) => {
      const selectedRange = context.workbook.getSelectedRange();
      const worksheet = selectedRange.worksheet;
      const topLeft = selectedRange.getCell(0, 0);
      worksheet.load('id');
      selectedRange.load('rowCount,columnCount');
      topLeft.load('address');
      await context.sync();
      assertNotAborted(signal);
      await assertCurrentDocumentIdentity(assertCurrentIdentity);
      if (
        selectedRange.rowCount < 1 ||
        selectedRange.columnCount < 1 ||
        selectedRange.rowCount > 500 ||
        selectedRange.columnCount > 30
      ) {
        throw targetDrift('Select a bounded empty Excel output range');
      }
      const address = String(topLeft.address || '')
        .split('!')
        .pop()
        .replace(/\$/g, '')
        .toUpperCase();
      if (!/^[A-Z]{1,3}[1-9]\d{0,6}$/.test(address)) {
        throw targetDrift('The selected Excel target could not be resolved');
      }
      return Object.freeze({
        app: 'excel',
        worksheetId: worksheet.id,
        anchorCell: address,
        requireEmpty: true,
      });
    });
  }

  async _loadTarget(context, request) {
    const worksheets = context.workbook.worksheets;
    worksheets.load('items/id');
    await context.sync();
    const matches = worksheets.items.filter((sheet) => sheet.id === request.target.worksheetId);
    if (matches.length !== 1) {
      throw targetDrift('The Excel target worksheet no longer exists');
    }
    const target = operationRange(request);
    const range = matches[0].getRange(target.address);
    range.load('address,rowCount,columnCount,values,formulas');
    await context.sync();
    if (
      range.rowCount !== target.cells.length ||
      range.columnCount !== target.cells[0].length
    ) {
      throw targetDrift('The Excel target range could not be resolved exactly');
    }
    return { sheet: matches[0], range, ...target };
  }

  async prepare(request, {
    signal,
    assertCurrentIdentity,
  } = {}) {
    assertNotAborted(signal);
    return this.run(async (context) => {
      const { range, cells } = await this._loadTarget(context, request);
      assertNotAborted(signal);
      await assertCurrentDocumentIdentity(assertCurrentIdentity);
      if (rangeState(range, cells) !== INSPECTION_STATUS.NOT_COMMITTED) {
        throw targetDrift('The Excel target range is not empty');
      }
      return {
        targetType: 'worksheet_range',
        stableTargetId: request.target.worksheetId,
      };
    });
  }

  async inspect(request, {
    signal,
    assertCurrentIdentity,
  } = {}) {
    assertNotAborted(signal);
    return this.run(async (context) => {
      const { range, cells } = await this._loadTarget(context, request);
      await assertCurrentDocumentIdentity(assertCurrentIdentity);
      return {
        status: rangeState(range, cells),
        mutationCount: cells.length * cells[0].length,
      };
    });
  }

  async commit(request, {
    signal,
    assertCurrentIdentity,
  } = {}) {
    assertNotAborted(signal);
    return this.run(async (context) => {
      const { range, cells } = await this._loadTarget(context, request);
      assertNotAborted(signal);
      await assertCurrentDocumentIdentity(assertCurrentIdentity);
      if (rangeState(range, cells) !== INSPECTION_STATUS.NOT_COMMITTED) {
        throw targetDrift('The Excel target range changed before commit');
      }
      await assertCurrentDocumentIdentity(assertCurrentIdentity);
      range.values = cells.map((row) =>
        row.map((value) => value === null ? '' : value)
      );
      await context.sync();
      range.load('values,formulas,rowCount,columnCount');
      await context.sync();
      if (
        range.rowCount !== cells.length ||
        range.columnCount !== cells[0].length ||
        rangeState(range, cells) !== INSPECTION_STATUS.COMMITTED
      ) {
        throw verificationFailed();
      }
      return {
        committed: true,
        mutationCount: cells.length * cells[0].length,
      };
    });
  }
}

export default ExcelFinanceWriter;
